import {
  TelegramTransactionPartByAmount,
  telegramAvailableFeaturesUserPromote,
  telegramTransactionsUserCheckStatus,
  telegramTransactionsUserCreate,
  telegramTransactionsUserGetPartsForAmount,
} from '@/generated';
import { confirmTelegramStarsPayment } from '@/utils/telegram-stars-payment';

export const PAYMENT_RETURN_PATH_KEY = 'doska.paymentReturnPath';

const savePaymentReturnPath = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const { pathname, search, hash } = window.location;
    const path = `${pathname}${search}${hash}`;
    localStorage.setItem(PAYMENT_RETURN_PATH_KEY, path);
  } catch (error) {
    console.warn('Unable to persist payment return path', error);
  }
};

type CardComParams = {
  payerName: string;
  parts: any;
  token?: string;
};

type HandleCoinsPurchaseParams = {
  methodId: string;
  amount: number;
} & Partial<CardComParams>;

type HandleCoinsPurchaseForFeaturesParams = {
  methodId: string;
  amount: number;
  productId: string;
  featureIds: string[];
} & Partial<CardComParams>;

type HandleFeaturePurchaseParams = {
  methodId: string;
  productId: string;
  featureIds: string[];
} & Partial<CardComParams>;

type HandleParams = HandleCoinsPurchaseParams | HandleCoinsPurchaseForFeaturesParams | HandleFeaturePurchaseParams;

interface Services {
  handlePayment(params: HandleCoinsPurchaseParams): Promise<void>;
  handlePayment(params: HandleCoinsPurchaseForFeaturesParams): Promise<void>;
  handlePayment(params: HandleFeaturePurchaseParams): Promise<any>;
  getPartsOptions(amount: number): Promise<TelegramTransactionPartByAmount[]>;
  getPartsOptions(featuresIds: string[]): Promise<TelegramTransactionPartByAmount[]>;
}

export const waitLong = () => {
  return new Promise<any>((resolve) => {
    setTimeout(() => {
      resolve(1);
    }, 100000);
  });
};

const completeCreatedTransaction = async (transaction: {
  id: string;
  isPaid?: boolean;
  link: string;
  openType: 'TELEGRAM' | 'WEB';
}) => {
  if (transaction.isPaid) {
    return;
  }

  if (!transaction.link) {
    throw new Error('Payment link is missing');
  }

  if (transaction.openType === 'TELEGRAM') {
    await confirmTelegramStarsPayment({
      transactionId: transaction.id,
      invoiceUrl: transaction.link,
      openInvoice: window.Telegram?.WebApp?.openInvoice
        ? (url, callback) => window.Telegram?.WebApp?.openInvoice?.(url, callback)
        : undefined,
      checkStatus: async (id) => (await telegramTransactionsUserCheckStatus({ id })).status,
    });
    return;
  }

  savePaymentReturnPath();
  window.location.href = transaction.link;
  await waitLong();
};

const getTransactionPaymentFields = (methodId: string) => {
  if (methodId === 'TELEGRAM_STARS') {
    return {
      paymentSystem: 'TELEGRAM_STARS' as const,
      platform: 'Telegram' as const,
    };
  }
  if (methodId === 'CARD_COM') {
    return {
      paymentSystem: 'CARD_COM' as const,
      platform: 'Web' as const,
    };
  }

  throw new Error(`Unsupported external payment system: ${methodId}`);
};

export const services: Services = {
  async handlePayment(params: HandleParams): Promise<void> {
    if ('methodId' in params && 'amount' in params && !('productId' in params)) {
      const { methodId, amount, payerName, token } = params;
      const payment = getTransactionPaymentFields(methodId);
      const res = await telegramTransactionsUserCreate({
        amount,
        ...payment,
        productId: '',
        tokenId: token,
        featureIds: [],
        rememberCard: methodId === 'CARD_COM' ? true : undefined,
        payerName: payerName,
      });
      await completeCreatedTransaction(res);
      return;
    }

    if ('methodId' in params && 'amount' in params && 'productId' in params && 'featureIds' in params) {
      const { methodId, amount, productId, featureIds, payerName, token } = params;
      const payment = getTransactionPaymentFields(methodId);
      const res = await telegramTransactionsUserCreate({
        amount,
        ...payment,
        productId,
        featureIds,
        rememberCard: methodId === 'CARD_COM' ? true : undefined,
        tokenId: token,
        payerName,
      });
      await completeCreatedTransaction(res);
      return;
    }

    if (!('amount' in params)) {
      const { methodId, productId, featureIds, token } = params;

      if (methodId === 'COINS' && !('parts' in params)) {
        const res = await telegramAvailableFeaturesUserPromote({ telegramProductId: productId, featureIds });
        return res;
      }

      const payment = getTransactionPaymentFields(methodId);
      const res = await telegramTransactionsUserCreate({
        ...payment,
        productId,
        featureIds,
        rememberCard: methodId === 'CARD_COM' ? true : undefined,
        payerName: params.payerName,
        tokenId: token,
      });

      await completeCreatedTransaction(res);
    }
  },
  async getPartsOptions(params: number | string[]): Promise<TelegramTransactionPartByAmount[]> {
    if (typeof params === 'number') {
      const res = await telegramTransactionsUserGetPartsForAmount({ amount: params });
      return res;
    }

    const res = await telegramTransactionsUserGetPartsForAmount({ featureIds: params });
    return res;
  },
};
